import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';

@Component({
  standalone: false,
  selector: 'app-chips-autocomplete',
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .chips-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .chips-label {
      color: #8f8f8f;
      font-size: 10px;
      line-height: 1.2;
    }

    .chips-form-field {
      display: block;
      margin: 0;
      --mdc-filled-text-field-container-color: transparent;
      --mdc-filled-text-field-focus-active-indicator-color: #a7a7a7;
      --mdc-filled-text-field-active-indicator-color: #a7a7a7;
      --mdc-filled-text-field-hover-active-indicator-color: #8f8f8f;
      --mat-form-field-container-height: auto;
      --mat-form-field-container-vertical-padding: 0;
    }

    .chips-grid {
      min-height: 24px;
    }

    .selection-chip {
      margin: 0;
      --mdc-chip-container-height: 24px;
      --mdc-chip-label-text-size: 13px;
      --mdc-chip-label-text-weight: 400;
      --mdc-chip-label-text-color: #343434;
      --mdc-chip-outline-color: transparent;
      --mdc-chip-elevated-container-color: #ededed;
      --mdc-chip-flat-container-color: #ededed;
      --mdc-chip-container-shape-radius: 12px;
    }

    .chip-remove-icon {
      position: relative;
      bottom: 0;
      font-size: 10px;
    }

    .chips-input {
      min-width: 84px;
      width: 84px;
      height: 24px;
      padding: 0;
      margin: 0;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      background: transparent !important;
      color: #343434;
      font-size: 13px;
      line-height: 24px;
    }

    .chips-input:focus,
    .chips-input:active {
      outline: none !important;
      box-shadow: none !important;
    }

    .loader {
      position: absolute;
      right: 0;
      bottom: 10px;
      color: #707070;
    }

    .loader:not(.shown) {
      display: none;
    }

    .btn-links {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      margin-top: 0;
      font-size: 13px;
      line-height: 1.3;
    }

    .btn-link {
      color: #6b6b6b;
      text-decoration: none;
      cursor: pointer;
    }

    .btn-link:hover {
      text-decoration: underline;
    }
  `],
  template: `
    <div class="chips-field">
      <div class="chips-label">{{ placeholderText }}</div>
      <mat-form-field class="w-100 chips-form-field" floatLabel="never" subscriptSizing="dynamic">
        <mat-chip-grid #chipGrid aria-label="{{ placeholderText }}" class="chips-grid">
          @for (val of selectedOptions; track val) {
<mat-chip-row class="selection-chip" (removed)="remove(val)">
            {{ val }}
            <fa-icon [icon]="icons.delete" matChipRemove class="ml-2 chip-remove-icon">cancel</fa-icon>
          </mat-chip-row>
}
          <input
            #input
            class="chips-input"
            [formControl]="control"
            [matAutocomplete]="auto"
            [matChipInputFor]="chipGrid"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
            [matChipInputAddOnBlur]="false"
            [attr.aria-label]="placeholderText">
        </mat-chip-grid>
        <fa-icon class="loader" [class.shown]="loading" [icon]="icons.falSpinnerThird" animation="spin"></fa-icon>
      </mat-form-field>
      <div class="btn-links">
        <a (click)="selectAll()" class="btn-link">{{'indicators.buttons.selectAll' | translate}} ({{placeholderLowerCase}})</a>
        <a (click)="unselectAll()" class="btn-link">{{'indicators.buttons.unselectAll' | translate}} ({{placeholderLowerCase}})</a>
      </div>
    </div>

    <mat-autocomplete #auto="matAutocomplete"
                      (optionSelected)="selected($event)"
                      [autoActiveFirstOption]="true"
                      aria-label="Select an option"
                      (opened)="onAutocompleteOpened()"
                      (closed)="onAutocompleteClosed()">
      <cdk-virtual-scroll-viewport itemSize="48" style="height: 256px">
        @for (option of filteredOptions | async; track option) {
<mat-option [value]="option" [tabindex]="-1"
        [id]="generateOptionId(option)"
        [attr.aria-selected]="isOptionSelected(option) ? 'true' : 'false'"
        (mouseenter)="updateActiveOption(generateOptionId(option))"
        (mouseleave)="updateActiveOption(null)">
          {{option}}
        </mat-option>
}
        @if (((filteredOptions | async) || []).length <= 0) {
<mat-option [disabled]="true">No data</mat-option>
}
        @if (isResultCropped) {
<mat-option [disabled]="true">Some results were not displayed, please be more specific in your search</mat-option>
}
      </cdk-virtual-scroll-viewport>
    </mat-autocomplete>
  `
})
export class ChipsAutocompleteComponent implements OnChanges {

  @Input() choices: string[] = [];
  @Input() placeholder: string;

  get placeholderText(): string {
    return this.placeholder || '';
  }

  get placeholderLowerCase(): string {
    return this.placeholderText.toLowerCase();
  }

  public _selectedOptions: string[] = [];
  @Output() selectedOptionsChange = new EventEmitter();

  get selectedOptions(): string[] {
    return this._selectedOptions;
  }

  @Input()
  set selectedOptions(vals: string[]) {
    this._selectedOptions = vals;
    this.selectedOptionsChange.emit(vals);
  }

  readonly icons = {
    delete: faTimes,
    falSpinnerThird: faSpinner,
  };
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly control = new UntypedFormControl(null);
  availableChoices: string[] = [];
  filteredOptions: Observable<string[]>;
  maxResults = 10;

  loading = false;
  hasTerms = false;
  isResultCropped = false;
  activeOptionId: string | null = null;

  @ViewChild('input') input: ElementRef;

  constructor(private renderer: Renderer2) {
    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(null as string),
      tap((val) => this.hasTerms = !!val),
      debounceTime(300),
      distinctUntilChanged((x, y) => !!x && !!y && x === y),
      tap(() => this.loading = true),
      map((terms: string | null) => {
        if (terms && terms.length >= 3) {
          return this.filter(terms);
        }
        const cropped = this.availableChoices.slice(0, 50);
        this.isResultCropped = cropped.length !== this.availableChoices.length;
        return cropped;
      }),
      tap(() => this.loading = false),
    );
  }

  ngAfterViewInit() {
    this.renderer.removeAttribute(this.input.nativeElement, 'autocomplete');
    this.renderer.setAttribute(this.input.nativeElement, 'role', 'combobox');
    this.renderer.setAttribute(this.input.nativeElement, 'aria-haspopup', 'listbox');
    this.renderer.setAttribute(this.input.nativeElement, 'aria-autocomplete', 'list');
    this.renderer.setAttribute(this.input.nativeElement, 'aria-expanded', 'false');
  }

  onAutocompleteOpened(): void {
    this.renderer.setAttribute(this.input.nativeElement, 'aria-expanded', 'true');
  }

  onAutocompleteClosed(): void {
    this.renderer.setAttribute(this.input.nativeElement, 'aria-expanded', 'false');
    this.renderer.removeAttribute(this.input.nativeElement, 'aria-activedescendant');
    this.activeOptionId = null;
  }

  updateActiveOption(optionId: string | null): void {
    this.activeOptionId = optionId;
    if (optionId) {
      document.getElementById(optionId)?.setAttribute('aria-selected', 'true');
      this.renderer.setAttribute(this.input.nativeElement, 'aria-activedescendant', optionId);
    } else {
      this.renderer.removeAttribute(this.input.nativeElement, 'aria-activedescendant');
    }
  }

  generateOptionId(option: string): string {
    return `option-${option.toLowerCase().replace(/\s/g, '-').replace(/[^\w-]/g, '')}`;
  }

  isOptionSelected(option: string): boolean {
    return this.activeOptionId === this.generateOptionId(option);
  }

  private resetAvailableChoices() {
    this.availableChoices = this.choices.filter((val) => this.selectedOptions.indexOf(val) === -1);
    this.control.setValue(null);
  }

  remove(val: string): void {
    this.selectedOptions = [...this.selectedOptions.filter((option) => option !== val)];
    this.resetAvailableChoices();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedOptions = [...this.selectedOptions, event.option.value];
    this.availableChoices = this.availableChoices.filter((val) => val !== event.option.value);
    this.control.setValue(null);
    this.input.nativeElement.value = '';
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    const results = this.availableChoices
      .filter((val) => !this.control.value || val !== this.control.value)
      .filter((val) => val.toLowerCase().indexOf(filterValue) === 0);

    const croppedResults = results.slice(0, this.maxResults);
    this.isResultCropped = results.length !== croppedResults.length;
    return croppedResults;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['choices']) {
      this.resetAvailableChoices();
    }
  }

  selectAll() {
    this.selectedOptions = [...this.choices];
    this.resetAvailableChoices();
  }

  unselectAll() {
    this.selectedOptions = [];
    this.resetAvailableChoices();
  }
}

